import { Card, Col, Row, Tooltip, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  EyeOutlined,
  InboxOutlined,
  MessageOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { totalViews, type DailyPoint, type MetricCounts } from '@entities/analytics';

const { Text } = Typography;

interface CardDef {
  key: 'views' | 'requests' | 'chats' | 'deals';
  icon: ReactNode;
  rgb: string;
  iconColor: string;
  value: (t: MetricCounts) => number;
}

const CARDS: CardDef[] = [
  { key: 'views', icon: <EyeOutlined />, rgb: '47,92,246', iconColor: '#2f5cf6', value: totalViews },
  {
    key: 'requests',
    icon: <InboxOutlined />,
    rgb: '245,158,11',
    iconColor: '#d97706',
    value: (t) => t.REQUEST_RECEIVED,
  },
  {
    key: 'chats',
    icon: <MessageOutlined />,
    rgb: '16,185,129',
    iconColor: '#10b981',
    value: (t) => t.CHAT_STARTED,
  },
  {
    key: 'deals',
    icon: <TrophyOutlined />,
    rgb: '139,92,246',
    iconColor: '#8b5cf6',
    value: (t) => t.DEAL_WON,
  },
];

/**
 * Change across the period: second half against first half of the daily series.
 * Returns null when there isn't enough data, or when the first half is zero —
 * a jump from 0 has no meaningful percentage and showing "+∞" or "+100%" would
 * be a lie dressed as a metric.
 */
function halfOverHalf(series: number[]): number | null {
  if (series.length < 4) return null;
  const mid = Math.floor(series.length / 2);
  const first = series.slice(0, mid).reduce((a, b) => a + b, 0);
  const second = series.slice(mid).reduce((a, b) => a + b, 0);
  if (first === 0) return null;
  return Math.round(((second - first) / first) * 100);
}

/** Inline sparkline — area fill plus an emphasised endpoint. No library needed. */
function Sparkline({ series, color }: { series: number[]; color: string }) {
  const w = 92;
  const h = 30;
  if (series.length < 2) return <svg width={w} height={h} aria-hidden />;

  const max = Math.max(...series);
  const min = Math.min(...series);
  const span = max - min || 1;
  const stepX = w / (series.length - 1);
  const y = (v: number) => h - 3 - ((v - min) / span) * (h - 6);

  const pts = series.map((v, i) => [i * stepX, y(v)] as const);
  const line = pts.map(([x, py], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${py.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const last = pts[pts.length - 1]!;
  const gid = `spark-${color.replace(/[^a-z0-9]/gi, '')}`;

  return (
    <svg width={w} height={h} aria-hidden style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
    </svg>
  );
}

interface KpiCardsProps {
  totals: MetricCounts;
  daily: DailyPoint[];
}

export function KpiCards({ totals, daily }: KpiCardsProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <Row gutter={[16, 16]}>
      {CARDS.map(({ key, icon, rgb, iconColor, value }) => {
        const series = daily.map((d) => value(d.counts));
        const delta = halfOverHalf(series);
        const tone =
          delta === null || delta === 0
            ? token.colorTextTertiary
            : delta > 0
              ? token.colorSuccess
              : token.colorError;
        const DeltaIcon =
          delta === null || delta === 0 ? ArrowRightOutlined : delta > 0 ? ArrowUpOutlined : ArrowDownOutlined;

        return (
          <Col xs={24} sm={12} lg={6} key={key}>
            <Card
              className="lift-on-hover"
              style={{
                borderRadius: 18,
                background: `linear-gradient(135deg, rgba(${rgb},0.16), rgba(${rgb},0) 70%)`,
                borderColor: token.colorBorderSecondary,
                height: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <Text style={{ fontSize: 13, fontWeight: 500, color: token.colorTextSecondary }}>
                    {t(`dashboard.kpi.${key}`)}
                  </Text>
                  <div
                    style={{
                      fontSize: 30,
                      fontWeight: 700,
                      lineHeight: 1.15,
                      letterSpacing: '-0.02em',
                      fontVariantNumeric: 'tabular-nums',
                      marginTop: 2,
                    }}
                  >
                    {value(totals).toLocaleString()}
                  </div>
                </div>
                <span
                  aria-hidden
                  style={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 12,
                    background: token.colorBgContainer,
                    color: iconColor,
                    fontSize: 19,
                    boxShadow: token.boxShadowTertiary,
                  }}
                >
                  {icon}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <Tooltip title={t('dashboard.kpi.deltaHint')}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        color: tone,
                        cursor: 'default',
                      }}
                    >
                      <DeltaIcon style={{ fontSize: 10 }} />
                      {delta === null ? t('dashboard.kpi.noDelta') : `${delta > 0 ? '+' : ''}${delta}%`}
                    </span>
                  </Tooltip>
                  <Text type="secondary" style={{ fontSize: 11.5 }}>
                    {t(`dashboard.kpi.${key}Hint`)}
                  </Text>
                </div>
                <Sparkline series={series} color={iconColor} />
              </div>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}
